import * as React from "react";
import { Search, Sparkles, Upload } from "lucide-react";
import {
  FACET_TITLES,
  facetLabel,
  SOURCE_LABELS,
  SOURCE_TITLE,
  TIER_LABELS,
  TIER_TITLE,
} from "@/components/Backgrounds/facetLabels";
import { Button, Combobox, Input, InputGroup, SimpleSelect } from "@/components/ui";
import { CoachOption } from "@/containers/Coach/PromptStudio/components/CoachPicker";
import { UPLOAD_TYPES } from "@/containers/Coach/PromptStudio/hooks/useBackgroundMutations";
import {
  BACKGROUND_FACETS,
  BackgroundFacet,
  BackgroundRatio,
  BackgroundSearchBody,
  BackgroundSource,
  BackgroundTier,
  BackgroundVisibility,
} from "@/services/bedrock/dtos/BackgroundAsset";

export type LibraryScope = "global" | "coach";
export type LibraryFlag = "hasPeople" | "hasText" | "hasLogo";
export type PreviewRatio = Extract<BackgroundRatio, "square_1_1" | "portrait_4_5" | "portrait_9_16">;

export interface LibraryFilters {
  q: string;
  facets: Partial<Record<BackgroundFacet, string[]>>;
  visibility: BackgroundVisibility[];
  source: BackgroundSource[];
  tier: BackgroundTier[];
  flags: LibraryFlag[];
  sort: "recent" | "popular";
  /** Crops the grid only; never sent. */
  previewRatio: PreviewRatio;
}

export const DEFAULT_LIBRARY_FILTERS: LibraryFilters = {
  q: "",
  facets: {},
  visibility: ["draft", "active"],
  source: [],
  tier: [],
  flags: [],
  sort: "recent",
  previewRatio: "portrait_4_5",
};

const LIBRARY_PAGE_SIZE = 50;

/** The search the grid runs for these filters. A toggled flag asks for assets that have it. */
export function librarySearchBody(
  filters: LibraryFilters,
  scope: LibraryScope,
  coach: CoachOption | null
): BackgroundSearchBody {
  const body: BackgroundSearchBody = { scope, sort: filters.sort, perPage: LIBRARY_PAGE_SIZE };
  if (scope === "coach" && coach) body.coachId = coach.id;
  if (filters.q) body.q = filters.q;
  (Object.keys(BACKGROUND_FACETS) as BackgroundFacet[]).forEach(facet => {
    const values = filters.facets[facet];
    if (values && values.length > 0) body[facet] = values;
  });
  if (filters.visibility.length > 0) body.visibility = filters.visibility;
  if (filters.source.length > 0) body.source = filters.source;
  if (filters.tier.length > 0) body.tier = filters.tier;
  filters.flags.forEach(flag => (body[flag] = true));
  return body;
}

interface Option {
  value: string;
  label: string;
}

const VISIBILITY_OPTIONS: Option[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];
const SOURCE_OPTIONS: Option[] = (Object.keys(SOURCE_LABELS) as BackgroundSource[]).map(value => ({
  value,
  label: SOURCE_LABELS[value],
}));
const TIER_OPTIONS: Option[] = (Object.keys(TIER_LABELS) as BackgroundTier[]).map(value => ({
  value,
  label: TIER_LABELS[value],
}));
const FLAGS: { flag: LibraryFlag; label: string }[] = [
  { flag: "hasPeople", label: "Has people" },
  { flag: "hasText", label: "Has text" },
  { flag: "hasLogo", label: "Has a logo" },
];
const SORT_ITEMS = [
  { value: "recent", label: "Newest" },
  { value: "popular", label: "Most used" },
];
export const PREVIEW_RATIO_ITEMS: { value: PreviewRatio; label: string }[] = [
  { value: "square_1_1", label: "1:1" },
  { value: "portrait_4_5", label: "4:5" },
  { value: "portrait_9_16", label: "9:16" },
];

const MultiChip: React.FC<{ title: string; options: Option[]; values: string[]; onChange(values: string[]): void }> = ({
  title,
  options,
  values,
  onChange,
}) => (
  <Combobox
    multiple
    items={options}
    value={options.filter(option => values.includes(option.value))}
    isItemEqualToValue={(option, selected) => option.value === selected?.value}
    itemToStringLabel={option => option.label}
    onValueChange={(selected: Option[] | null) => onChange((selected ?? []).map(option => option.value))}
    placeholder={title}
    className="w-auto min-w-36 bg-white"
    maxChips={1}
  />
);

/** A labeled `SimpleSelect`; the label names the group, since the select's trigger takes no label. */
export const OptionSelect: React.FC<{
  label: string;
  items: { value: string; label: string }[];
  value: string;
  onChange(value: string): void;
}> = ({ label, items, value, onChange }) => (
  <div
    role="group"
    aria-label={label}
    className="flex h-9 items-center rounded-md border border-input bg-white px-3 text-sm"
  >
    <SimpleSelect items={items} value={value} onValueChange={next => onChange(next ?? "")} />
  </div>
);

export interface LibraryActionsProps {
  /** A coach library needs a coach to write into. */
  canWrite: boolean;
  onGenerate(): void;
  onUpload(file: File): void;
  uploading: boolean;
}

/** Create with AI and Upload, shown in the section header. */
export const LibraryActions: React.FC<LibraryActionsProps> = ({ canWrite, onGenerate, onUpload, uploading }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) onUpload(file);
  };

  return (
    <>
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={!canWrite} loading={uploading}>
        <Upload aria-hidden="true" />
        Upload
      </Button>
      <Button onClick={onGenerate} disabled={!canWrite}>
        <Sparkles aria-hidden="true" />
        Create with AI
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_TYPES.join(",")}
        className="hidden"
        onChange={onFile}
        data-testid="library-upload-input"
      />
    </>
  );
};

export interface LibraryToolbarProps {
  filters: LibraryFilters;
  onFiltersChange(filters: LibraryFilters): void;
}

const SEARCH_DEBOUNCE_MS = 300;

export const LibraryToolbar: React.FC<LibraryToolbarProps> = ({ filters, onFiltersChange }) => {
  const [search, setSearch] = React.useState(filters.q);
  const latest = React.useRef({ filters, onFiltersChange });
  latest.current = { filters, onFiltersChange };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const q = search.trim();
      if (q !== latest.current.filters.q) latest.current.onFiltersChange({ ...latest.current.filters, q });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const set = <K extends keyof LibraryFilters>(key: K, value: LibraryFilters[K]) =>
    onFiltersChange({ ...filters, [key]: value });

  const toggleFlag = (flag: LibraryFlag) =>
    set("flags", filters.flags.includes(flag) ? filters.flags.filter(f => f !== flag) : [...filters.flags, flag]);

  return (
    <div className="flex flex-col gap-3" data-testid="library-toolbar">
      <InputGroup
        className="max-w-xl"
        control={controlProps => (
          <Input
            {...controlProps}
            value={search}
            placeholder="Search backgrounds…"
            aria-label="Search backgrounds"
            onChange={event => setSearch(event.target.value)}
          />
        )}
        prepend={<Search />}
      />

      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(BACKGROUND_FACETS) as BackgroundFacet[]).map(facet => (
          <MultiChip
            key={facet}
            title={FACET_TITLES[facet]}
            options={BACKGROUND_FACETS[facet].map(value => ({ value, label: facetLabel(facet, value) }))}
            values={filters.facets[facet] ?? []}
            onChange={values => set("facets", { ...filters.facets, [facet]: values })}
          />
        ))}
        <MultiChip
          title="Visibility"
          options={VISIBILITY_OPTIONS}
          values={filters.visibility}
          onChange={values => set("visibility", values as BackgroundVisibility[])}
        />
        <MultiChip
          title={SOURCE_TITLE}
          options={SOURCE_OPTIONS}
          values={filters.source}
          onChange={values => set("source", values as BackgroundSource[])}
        />
        <MultiChip
          title={TIER_TITLE}
          options={TIER_OPTIONS}
          values={filters.tier}
          onChange={values => set("tier", values as BackgroundTier[])}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FLAGS.map(({ flag, label }) => {
          const on = filters.flags.includes(flag);
          return (
            <Button
              key={flag}
              size="sm"
              variant={on ? "secondary" : "outline"}
              aria-pressed={on}
              onClick={() => toggleFlag(flag)}
            >
              {label}
            </Button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <OptionSelect
            label="Sort"
            items={SORT_ITEMS}
            value={filters.sort}
            onChange={value => set("sort", value as LibraryFilters["sort"])}
          />
          <OptionSelect
            label="Preview ratio"
            items={PREVIEW_RATIO_ITEMS}
            value={filters.previewRatio}
            onChange={value => set("previewRatio", value as PreviewRatio)}
          />
        </div>
      </div>
    </div>
  );
};
